import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DT } from '../../theme/designTokens';
import { useFriends } from '../../context/FriendContext';
import { apiClient } from '../../network/ApiClient';
import { showSocialToast } from './SocialToastManager';
import type { GameInvitation } from '../../types/social';
import { CountryFlag } from '../ui/CountryFlag';
import { useTranslation } from '../../i18n/react';

const AUTO_DISMISS_MS = 30_000;
const ANIM_MS         = 280;

// ─── Toast display logic ──────────────────────────────────────────────────────
//
// Watches incomingInvitations from FriendContext. Each new invitation id
// is pushed into a FIFO queue. One toast is visible at a time.
// Accept navigates to /lobby/:sessionId. Decline calls declineInvitation.
// Auto-dismisses after 30 s and advances the queue.

export function GameInvitationToast() {
  const navigate = useNavigate();
  const { t } = useTranslation('play');
  const { incomingInvitations, acceptInvitation, declineInvitation, refresh } = useFriends();

  const [visible, setVisible]   = useState<GameInvitation | null>(null);
  const [queue, setQueue]       = useState<GameInvitation[]>([]);
  const [entered, setEntered]   = useState(false); // drives enter animation

  const [busy, setBusy] = useState(false);

  const seenIds      = useRef<Set<number>>(new Set());
  const dismissTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const animTimer    = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ── Sync new invitations into queue ──────────────────────────────────────

  useEffect(() => {
    const fresh = incomingInvitations.filter(inv => !seenIds.current.has(inv.id));
    if (fresh.length === 0) return;
    for (const inv of fresh) seenIds.current.add(inv.id);
    setQueue(q => [...q, ...fresh]);
  }, [incomingInvitations]);

  // ── Advance queue when slot is free ──────────────────────────────────────

  useEffect(() => {
    if (visible !== null || queue.length === 0) return;
    const [next, ...rest] = queue;
    setQueue(rest);
    setVisible(next);
    setEntered(false);
    // Defer one frame so CSS transition sees the initial off-screen state
    const raf = requestAnimationFrame(() => setEntered(true));
    return () => cancelAnimationFrame(raf);
  }, [visible, queue]);

  // ── Auto-dismiss ──────────────────────────────────────────────────────────

  useEffect(() => {
    if (visible === null) return;
    if (dismissTimer.current) clearTimeout(dismissTimer.current);
    dismissTimer.current = setTimeout(dismiss, AUTO_DISMISS_MS);
    return () => { if (dismissTimer.current) clearTimeout(dismissTimer.current); };
  // dismiss is stable (defined below with no captured state that changes)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  // ── External removal (expired / declined elsewhere) ───────────────────────

  useEffect(() => {
    if (visible === null) return;
    const stillExists = incomingInvitations.some(inv => inv.id === visible.id);
    if (!stillExists) dismiss();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [incomingInvitations]);

  // ── Dismiss helper ────────────────────────────────────────────────────────

  function dismiss() {
    setEntered(false);
    setBusy(false);
    if (animTimer.current) clearTimeout(animTimer.current);
    animTimer.current = setTimeout(() => setVisible(null), ANIM_MS);
  }

  // ── Actions ───────────────────────────────────────────────────────────────

  async function handleJoin() {
    if (!visible || busy) return;
    setBusy(true);
    const { id, session } = visible;
    try {
      await acceptInvitation(id);
      await apiClient.joinSession(session.id);
      dismiss();
      navigate(`/lobby/${session.id}`);
    } catch {
      showSocialToast(t('invitation.joinError'));
      dismiss();
      void refresh();
    }
  }

  async function handleDecline() {
    if (!visible || busy) return;
    setBusy(true);
    dismiss();
    try { await declineInvitation(visible.id); } catch { /* context handles error state */ }
  }

  // ── Render ────────────────────────────────────────────────────────────────

  if (!visible) return null;

  return (
    <div
      role="alertdialog"
      aria-live="assertive"
      aria-label={t('invitation.aria', { name: visible.from_user.display_name })}
      style={{
        position:        'fixed',
        bottom:          'max(24px, env(safe-area-inset-bottom, 0px) + 16px)',
        left:            '50%',
        transform:       `translateX(-50%) translateY(${entered ? '0%' : '120%'})`,
        transition:      `transform ${ANIM_MS}ms ${DT.motion.easing}`,
        zIndex:          40,
        width:           'min(400px, calc(100vw - 32px))',
        backgroundColor: DT.color.cardBgElevated,
        border:          `1px solid ${DT.color.borderGold}`,
        borderRadius:    DT.radius.lg,
        padding:         '16px 20px',
        boxShadow:       '0 8px 32px rgba(0,0,0,0.5)',
        display:         'flex',
        flexDirection:   'column',
        gap:             12,
      }}
    >
      {/* Sender + session info */}
      <div>
        <p style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 6, fontSize: 14, fontWeight: 700, color: DT.color.textPrimary, fontFamily: DT.font.sans }}>
          {/* Player Identity V1: invitations are always from a friend (human) — flag always applies. */}
          <CountryFlag countryCode={visible.from_user.country_code} width={16} />
          {/* Whole sentence with the sender's name interpolated verbatim. */}
          <span>{t('invitation.invitedYou', { name: visible.from_user.display_name })}</span>
        </p>
        <p style={{ margin: '3px 0 0', fontSize: 12, color: DT.color.textSecondary, fontFamily: DT.font.sans }}>
          {t('invitation.playersWaiting', { count: visible.session.player_count })}
        </p>
      </div>

      {/* Actions */}
      <div style={{ display: 'flex', gap: 8 }}>
        <button
          onClick={handleJoin}
          disabled={busy}
          style={{
            flex:                    1,
            padding:                 '9px 0',
            backgroundColor:         DT.color.gold,
            color:                   DT.color.goldOnGold,
            border:                  'none',
            borderRadius:            DT.radius.sm,
            fontSize:                13,
            fontWeight:              700,
            fontFamily:              DT.font.sans,
            cursor:                  busy ? 'default' : 'pointer',
            opacity:                 busy ? 0.6 : 1,
            WebkitTapHighlightColor: 'transparent',
          }}
        >
          {busy ? t('invitation.joining') : t('invitation.join')}
        </button>
        <button
          onClick={handleDecline}
          disabled={busy}
          style={{
            flex:                    1,
            padding:                 '9px 0',
            backgroundColor:         'rgba(255,255,255,0.06)',
            color:                   DT.color.textSecondary,
            border:                  'none',
            borderRadius:            DT.radius.sm,
            fontSize:                13,
            fontWeight:              600,
            fontFamily:              DT.font.sans,
            cursor:                  busy ? 'default' : 'pointer',
            opacity:                 busy ? 0.5 : 1,
            WebkitTapHighlightColor: 'transparent',
          }}
        >
          {t('invitation.decline')}
        </button>
      </div>
    </div>
  );
}
